import { useState, useRef, useCallback } from 'react'
import { ColumnManager } from '../../../../../utils/ColumnManager'
import { eventBus, EVENTS } from '../../../../../utils/EventBus'
import { validateCustomerCodes } from '../../../../../services/Draft'
import {
  validateCustomerCodeLocal,
  normalizeCustomerCode
} from '../../../../../utils/customerValidation'

export function useColumnAddition(
  columnManager,
  distributionManager,
  setNewColumnId,
  scrollToRight,
  sellerCode,
  selectedItems
) {
  const [showAddInput, setShowAddInput] = useState(false)
  const [newCustomerCode, setNewCustomerCode] = useState('')
  const [isDisabled, setIsDisabled] = useState('')
  const addInputRef = useRef(null)

  const handleAddNewColumn = useCallback(() => {
    const tempCode = `NEW_${Date.now()}`
    const success = columnManager.addCustomerColumn(tempCode, '')

    if (success) {
      distributionManager.addCustomerToItems(selectedItems, tempCode, '')

      const col = columnManager.getColumnByCustomerCode(tempCode)
      const snapshot = {
        colId: col.id,
        col: { ...col },
        width: columnManager.getWidth(col.id),
        orderSnapshot: [...columnManager.order],
        headerFields: columnManager.getCustomerHeaderFields(tempCode)
      }

      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] 新しい列を追加しました`,
        up: () => {
          if (!columnManager.hasCustomerCode(tempCode)) {
            columnManager.restoreCustomerColumn(snapshot)
            distributionManager.addCustomerToItems(selectedItems, tempCode, '')
            eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
          }
        },
        down: () => {
          columnManager.removeCustomerColumn(tempCode)
          distributionManager.removeCustomerFromItems(selectedItems, tempCode)
        }
      })

      setNewColumnId(tempCode)
      scrollToRight()
    }
  }, [
    columnManager,
    distributionManager,
    selectedItems,
    setNewColumnId,
    scrollToRight
  ])

  const handleShowAddInput = useCallback(() => {
    setShowAddInput(true)
  }, [])

  const handleAddColumn = useCallback(async () => {
    const rawCode = newCustomerCode.trim()
    if (!rawCode) {
      setShowAddInput(false)
      setNewCustomerCode('')
      return
    }

    const code = normalizeCustomerCode(rawCode)
    if (code !== rawCode) {
      setNewCustomerCode(code)
    }

    const localError = validateCustomerCodeLocal(code)
    if (localError) {
      alert(localError)
      return
    }

    try {
      const validation = await validateCustomerCodes([code], sellerCode)
      if (!validation?.valid) {
        const firstError =
          validation?.errors?.[code] ||
          Object.values(validation?.errors || {})[0] ||
          '顧客コードが無効です。'
        alert(firstError)
        return
      }
    } catch (error) {
      alert('顧客コードの確認に失敗しました。')
      return
    }

    const usedIndexes = new Set(
      columnManager.getCustomerColumns()
        .map((col) => ColumnManager.parseColumnKey(col.customerCode))
        .filter(({ customerCode, branchCode }) => customerCode === code && !branchCode)
        .map(({ index }) => index)
    )
    let index = 0
    while (usedIndexes.has(index)) index += 1
    const columnKey = ColumnManager.buildColumnKey(code, '', index)

    const success = columnManager.addCustomerColumn(columnKey, code)

    if (success) {
      distributionManager.addCustomerToItems(selectedItems, columnKey, 0)
      setNewCustomerCode('')
      setShowAddInput(false)

      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] 顧客列を追加しました：${code}`,
        up: () => {},
        down: () => {}
      })

      scrollToRight()
    }
  }, [
    newCustomerCode,
    columnManager,
    distributionManager,
    selectedItems,
    scrollToRight,
    sellerCode
  ])

  const handleInputKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleAddColumn()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setShowAddInput(false)
        setNewCustomerCode('')
      }
    },
    [handleAddColumn]
  )

  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      if (newCustomerCode.trim()) {
        handleAddColumn()
      } else {
        setShowAddInput(false)
        setNewCustomerCode('')
      }
    }, 100)
  }, [newCustomerCode, handleAddColumn])

  return {
    showAddInput,
    newCustomerCode,
    setNewCustomerCode,
    isDisabled,
    setIsDisabled,
    addInputRef,
    handleAddNewColumn,
    handleShowAddInput,
    handleAddColumn,
    handleInputKeyDown,
    handleInputBlur
  }
}
